import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Button,
  Avatar,
  Body1,
  Caption1,
  Badge,
  Divider,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
} from '@fluentui/react-components';
import {
  Board24Regular,
  People24Regular,
  Briefcase24Regular,
  Apps24Regular,
  Grid24Regular,
  Money24Regular,
  Receipt24Regular,
  Settings24Regular,
  SignOut24Regular,
  PlugConnected24Regular,
  TaskListSquarePerson24Regular,
  PersonKey24Regular,
  History24Regular,
  MoreHorizontal24Regular,
  Home24Filled,
  Home24Regular,
  Briefcase24Filled,
  People24Filled,
  Grid24Filled,
  Phone24Regular,
  Search24Regular,
  WeatherSunny24Regular,
  WeatherMoon24Regular,
  Settings24Regular as ThemeAutoIcon,
} from '@fluentui/react-icons';
import { useAuth } from '@/hooks/useAuth';
import { getInitials } from '@/utils/formatters';
import { CommandPalette } from '@/components/CommandPalette';
import { StatusBar } from '@/components/StatusBar';
import { useThemeMode, type ThemeMode } from '@/theme/themeMode';
import type { UserRole } from '@/models/types';

interface NavItem {
  path: string;
  label: string;
  icon: JSX.Element;
  iconFilled?: JSX.Element;
  minRole?: UserRole;
}

// Full list for sidebar (desktop) and "More" page (mobile)
const ALL_NAV: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: <Board24Regular />, iconFilled: <Home24Filled /> },
  { path: '/members', label: 'Members', icon: <People24Regular />, iconFilled: <People24Filled />, minRole: 'RO' },
  { path: '/projects', label: 'Projects', icon: <Briefcase24Regular />, iconFilled: <Briefcase24Filled /> },
  { path: '/applications', label: 'Applications', icon: <Apps24Regular /> },
  { path: '/allocations', label: 'Allocations', icon: <TaskListSquarePerson24Regular />, minRole: 'RO' },
  { path: '/access', label: 'Access Matrix', icon: <Grid24Regular />, iconFilled: <Grid24Filled />, minRole: 'RO' },
  { path: '/user-access', label: 'User Access', icon: <PersonKey24Regular />, minRole: 'RO' },
  { path: '/costs', label: 'Cost Center', icon: <Money24Regular />, minRole: 'RO' },
  { path: '/expenses', label: 'Expenses', icon: <Receipt24Regular /> },
  { path: '/integrations', label: 'Integrations', icon: <PlugConnected24Regular />, minRole: 'Admin' },
  { path: '/audit', label: 'Audit Log', icon: <History24Regular />, minRole: 'Admin' },
  { path: '/devices', label: 'Devices', icon: <Phone24Regular />, minRole: 'Admin' },
  { path: '/cost-allocation', label: 'Cost Allocation', icon: <Money24Regular />, minRole: 'Admin' },
  { path: '/settings', label: 'Settings', icon: <Settings24Regular />, minRole: 'Admin' },
];

// Only 5 items on the bottom tab bar for mobile (iOS convention)
const BOTTOM_TABS: NavItem[] = [
  { path: '/', label: 'Home', icon: <Home24Regular />, iconFilled: <Home24Filled /> },
  { path: '/projects', label: 'Projects', icon: <Briefcase24Regular />, iconFilled: <Briefcase24Filled /> },
  { path: '/expenses', label: 'Expenses', icon: <Receipt24Regular /> },
  { path: '/members', label: 'People', icon: <People24Regular />, iconFilled: <People24Filled />, minRole: 'RO' },
  { path: '/more', label: 'More', icon: <MoreHorizontal24Regular /> },
];

const ROLE_LEVEL: Record<UserRole, number> = { Member: 0, RO: 1, Admin: 2 };

function canAccessNavItem(item: NavItem, userRole: UserRole): boolean {
  if (!item.minRole) return true;
  return ROLE_LEVEL[userRole] >= ROLE_LEVEL[item.minRole];
}

const ROLE_COLORS: Record<string, 'brand' | 'success' | 'informative'> = {
  Admin: 'brand',
  RO: 'success',
  Member: 'informative',
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // ─── Desktop sidebar ────────────────────────────────────────────────────
  sidebar: {
    width: '260px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'fixed' as const,
    top: 0, left: 0, bottom: 0,
    zIndex: 100,
    '@media (max-width: 768px)': { display: 'none' },
  },
  logo: {
    padding: '20px 20px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logoText: {
    fontSize: '18px',
    fontWeight: 700,
    color: tokens.colorBrandForeground1,
  },
  nav: {
    flex: 1,
    padding: '8px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    overflowY: 'auto' as const,
  },
  navItem: {
    justifyContent: 'flex-start',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: tokens.borderRadiusMedium,
    width: '100%',
    fontWeight: 400,
    minHeight: '40px',
  },
  navItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
  },
  userSection: {
    padding: '12px 16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  userInfo: { flex: 1, minWidth: 0 },
  userName: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  // ─── Main content ───────────────────────────────────────────────────────
  main: {
    flex: 1,
    minWidth: 0, // prevent flex children from pushing past parent width
    marginLeft: '260px',
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 768px)': {
      marginLeft: 0,
      paddingBottom: '64px', // space for bottom tab bar
    },
  },
  // ─── Mobile top bar (iOS-style large title area) ────────────────────────
  // CRITICAL: padding-top must respect iOS safe area inset (status bar
  // height) otherwise the logo + title render *under* the iOS clock /
  // signal / battery icons. `env(safe-area-inset-top)` is the iOS-aware
  // CSS value Capacitor exposes on iPhone X+ devices. Fallback to 0 on
  // notch-less devices and the web.
  topbar: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))',
      paddingRight: '16px',
      paddingBottom: '10px',
      paddingLeft: '16px',
      backgroundColor: tokens.colorNeutralBackground1,
      borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
      position: 'sticky' as const,
      top: 0,
      zIndex: 50,
      backdropFilter: 'saturate(180%) blur(20px)',
      // Slight extra height so the title doesn't hug the status bar
      minHeight: 'calc(56px + env(safe-area-inset-top, 0px))',
    },
  },
  topbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  topbarTitle: {
    fontWeight: 700,
    fontSize: '22px',
    color: tokens.colorBrandForeground1,
    letterSpacing: '-0.5px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    maxWidth: 'calc(100vw - 110px)',
  },
  content: {
    flex: 1,
    padding: '24px',
    maxWidth: '1400px',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box' as const,
    overflowX: 'hidden' as const, // hard guard: nothing inside can horizontally overflow the viewport
    '@media (max-width: 768px)': {
      // Bottom padding accounts for the fixed bottom tab bar (56px) +
      // home-indicator safe area, so content doesn't slide under it.
      padding: '16px 16px calc(72px + env(safe-area-inset-bottom, 0px))',
    },
  },
  // ─── Bottom tab bar (mobile, iOS-style) ─────────────────────────────────
  bottomTabs: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      position: 'fixed' as const,
      bottom: 0, left: 0, right: 0,
      backgroundColor: tokens.colorNeutralBackground1,
      borderTop: `0.5px solid ${tokens.colorNeutralStroke2}`,
      zIndex: 100,
      paddingBottom: 'env(safe-area-inset-bottom)',
      backdropFilter: 'saturate(180%) blur(20px)',
      height: '56px',
    },
  },
  tabButton: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    padding: '6px 4px 2px',
    color: tokens.colorNeutralForeground3,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '10px',
    fontWeight: 500,
    minHeight: '52px',
  },
  tabButtonActive: {
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
  },
  tabLabel: {
    fontSize: '10px',
  },
});

export function Layout() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const currentItem = ALL_NAV.find((i) => i.path === location.pathname);
  const currentTitle = currentItem?.label ?? 'Prism';

  // ── Command palette (Cmd/Ctrl+K) ────────────────────────────────────
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isModK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isModK) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      } else if (e.key === '/' && document.activeElement === document.body) {
        // "/" anywhere outside an input also opens it (vim-style)
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={styles.root}>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
      {/* ── Desktop sidebar ────────────────────────────────────────── */}
      <aside className={styles.sidebar}>
        <div className={styles.logo}>
          <svg width="28" height="28" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <polygon points="16,2 30,28 2,28" fill="#5B92E5" opacity="0.85"/>
            <polygon points="16,7 26,26 6,26" fill="#D8A878" opacity="0.6"/>
            <polygon points="16,12 22,24 10,24" fill="#5B92E5" opacity="0.4"/>
          </svg>
          <span className={styles.logoText}>Prism</span>
        </div>
        <Divider />
        <nav className={styles.nav}>
          {ALL_NAV.filter((i) => !user || canAccessNavItem(i, user.role)).map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Button
                key={item.path}
                appearance="subtle"
                icon={item.icon}
                className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
                onClick={() => navigate(item.path)}
              >
                {item.label}
              </Button>
            );
          })}
        </nav>
        {user && (
          <div className={styles.userSection}>
            <Avatar name={user.displayName} initials={getInitials(user.displayName)} size={36} color="brand" />
            <div className={styles.userInfo}>
              <Body1 className={styles.userName}>{user.displayName}</Body1>
              <Caption1>
                <Badge appearance="filled" color={ROLE_COLORS[user.role] ?? 'informative'} size="small">
                  {user.role}
                </Badge>
              </Caption1>
            </div>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button appearance="subtle" icon={<SignOut24Regular />} size="small" />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem icon={<SignOut24Regular />} onClick={logout}>
                    Sign out
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        )}
      </aside>

      {/* ── Main area ──────────────────────────────────────────────── */}
      <main className={styles.main}>
        {/* Mobile top bar: logo + large title + avatar */}
        <div className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <svg width="24" height="24" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <polygon points="16,2 30,28 2,28" fill="#5B92E5" opacity="0.85"/>
              <polygon points="16,7 26,26 6,26" fill="#D8A878" opacity="0.6"/>
              <polygon points="16,12 22,24 10,24" fill="#5B92E5" opacity="0.4"/>
            </svg>
            <span className={styles.topbarTitle}>{currentTitle}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              appearance="subtle"
              icon={<Search24Regular />}
              onClick={() => setPaletteOpen(true)}
              aria-label="Search (⌘K)"
              title="Search (⌘K)"
            />
            {user && (
              <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Avatar
                  name={user.displayName}
                  initials={getInitials(user.displayName)}
                  size={32}
                  color="brand"
                  style={{ cursor: 'pointer' }}
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem>
                    <div>
                      <Body1 style={{ fontWeight: 600 }}>{user.displayName}</Body1>
                      <Caption1>{user.email}</Caption1>
                    </div>
                  </MenuItem>
                  <ThemeMenuItem />
                  <MenuItem icon={<SignOut24Regular />} onClick={logout}>
                    Sign out
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
            )}
          </div>
        </div>

        <div className={styles.content}>
          <Outlet />
        </div>
      </main>

      {/* ── Bottom tab bar (mobile) ────────────────────────────────── */}
      <nav className={styles.bottomTabs}>
        {BOTTOM_TABS.filter((t) => !user || canAccessNavItem(t, user.role)).map((tab) => {
          const isActive =
            tab.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(tab.path);
          return (
            <button
              key={tab.path}
              className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ''}`}
              onClick={() => navigate(tab.path)}
            >
              {isActive && tab.iconFilled ? tab.iconFilled : tab.icon}
              <span className={styles.tabLabel}>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      <StatusBar />
    </div>
  );
}

/**
 * Cycles through Light → Dark → Auto in the avatar menu. Auto follows
 * the OS prefers-color-scheme. Persists in localStorage.
 */
function ThemeMenuItem() {
  const { mode, setMode } = useThemeMode();
  const order: ThemeMode[] = ['light', 'dark', 'auto'];
  const next = order[(order.indexOf(mode) + 1) % order.length];

  const icon =
    mode === 'light' ? (
      <WeatherSunny24Regular />
    ) : mode === 'dark' ? (
      <WeatherMoon24Regular />
    ) : (
      <ThemeAutoIcon />
    );

  const label =
    mode === 'light'
      ? 'Theme: Light'
      : mode === 'dark'
        ? 'Theme: Dark'
        : 'Theme: Auto';

  return (
    <MenuItem icon={icon} onClick={() => setMode(next)}>
      {label} · tap for {next}
    </MenuItem>
  );
}
